import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import axios from "axios";
import { VolunteersFAQService } from "../../services/volunteersFAQService";
import { VolunteersFAQValidator } from "../../validators/volunteersFAQValidator";
import { getErrorMessage } from "../../utils/errorUtils";
import { toComparableRichText } from "../../utils/richText";
import { ApiResponse, OperationStatus } from "../VolunteersHighlightContext/constants";
import {
	getDefaultVolunteersFAQFormData,
	toVolunteersFAQEditFormData,
	UpdateVolunteersFAQLocalizable,
	UpdateVolunteersFAQRequest,
	VOLUNTEERS_FAQ_LIST_PATH,
	VolunteersFAQDetail,
	VolunteersFAQEditFormData,
	VolunteersFAQFormData,
	VolunteersFAQListItem,
} from "./constants";

const NOT_FOUND_MESSAGE = "Merak edilen soru bulunamadı.";

interface VolunteersFAQContextState {
	// Core Domain Data
	volunteersFAQList: VolunteersFAQListItem[];
	volunteersFAQDetail: VolunteersFAQDetail | null;
	volunteersFAQForm: VolunteersFAQFormData;
	volunteersFAQEditForm: VolunteersFAQEditFormData | null;
	operationStatus: OperationStatus;

	// Setters
	setVolunteersFAQForm: React.Dispatch<React.SetStateAction<VolunteersFAQFormData>>;
	setVolunteersFAQEditForm: React.Dispatch<React.SetStateAction<VolunteersFAQEditFormData | null>>;
	clearVolunteersFAQFormData: () => void;
	clearVolunteersFAQList: () => void;

	// Actions
	getVolunteersFAQList: () => Promise<void>;
	getVolunteersFAQDetails: (id: string) => Promise<void>;
	addVolunteersFAQ: () => Promise<void>;
	updateVolunteersFAQ: () => Promise<void>;
	removeVolunteersFAQ: (id: string) => Promise<void>;
}

interface VolunteersFAQProviderProps {
	children: React.ReactNode;
}

const VolunteersFAQContext = createContext<VolunteersFAQContextState | undefined>(undefined);

export const useVolunteersFAQContext = () => {
	const context = useContext(VolunteersFAQContext);
	if (!context) {
		throw new Error("useVolunteersFAQContext must be used within a VolunteersFAQContextProvider");
	}
	return context;
};

const getResponseErrorMessage = (response: ApiResponse<unknown>) => {
	const error = response.error;
	if (typeof error === "string") {
		return error;
	}
	return error?.message || "Beklenmedik bir hata oluştu.";
};

const isNotFoundError = (error: unknown) => axios.isAxiosError(error) && error.response?.status === 404;

const showError = (error: unknown) => {
	console.error(error);
	message.error(isNotFoundError(error) ? NOT_FOUND_MESSAGE : getErrorMessage(error));
};

/* -------------- Build Update Request (Only Changed Fields) -------------- */

export const buildVolunteersFAQUpdateRequest = (
	current: VolunteersFAQEditFormData,
	original: VolunteersFAQEditFormData
): UpdateVolunteersFAQRequest => {
	const request: UpdateVolunteersFAQRequest = { faqId: current.faqId };

	if (current.isActive !== original.isActive) {
		request.isActive = current.isActive;
	}
	if (current.displayOrder !== original.displayOrder) {
		request.displayOrder = current.displayOrder ?? null;
	}

	const changedLocalizables = current.volunteersFAQLocalizable.flatMap(item => {
		const before = original.volunteersFAQLocalizable.find(o => o.culture === item.culture);
		const changed: UpdateVolunteersFAQLocalizable = { culture: item.culture };
		if (item.faqLocalizableId) {
			changed.volunteersFAQLocalizableId = item.faqLocalizableId;
		}
		let hasChange = false;

		if (item.question.trim() !== (before?.question ?? "").trim()) {
			changed.question = item.question;
			hasChange = true;
		}
		// The editor writes the loaded answer back in its own HTML form, which alone is not a change.
		if (toComparableRichText(item.answer) !== toComparableRichText(before?.answer ?? "")) {
			changed.answer = item.answer;
			hasChange = true;
		}

		return hasChange ? [changed] : [];
	});

	if (changedLocalizables.length > 0) {
		request.volunteersFAQLocalizable = changedLocalizables;
	}

	return request;
};

export const VolunteersFAQContextProvider: React.FC<VolunteersFAQProviderProps> = ({ children }) => {
	const navigate = useNavigate();
	const [volunteersFAQList, setVolunteersFAQList] = useState<VolunteersFAQListItem[]>([]);
	const [volunteersFAQDetail, setVolunteersFAQDetail] = useState<VolunteersFAQDetail | null>(null);
	const [volunteersFAQForm, setVolunteersFAQForm] = useState<VolunteersFAQFormData>(getDefaultVolunteersFAQFormData);
	const [volunteersFAQEditForm, setVolunteersFAQEditForm] = useState<VolunteersFAQEditFormData | null>(null);
	const [operationStatus, setOperationStatus] = useState<OperationStatus>({ type: "idle" });
	// The loading state only disables the buttons after a re-render, so a fast double click could still send a
	// second request; this ref blocks overlapping add/update/delete calls synchronously.
	const isMutatingRef = useRef(false);

	/* -------------- Clear helpers -------------- */

	const clearVolunteersFAQFormData = useCallback(() => {
		setVolunteersFAQForm(getDefaultVolunteersFAQFormData());
		setVolunteersFAQEditForm(null);
		setVolunteersFAQDetail(null);
	}, []);

	const clearVolunteersFAQList = useCallback(() => {
		setVolunteersFAQList([]);
	}, []);

	/* -------------- Actions -------------- */

	const getVolunteersFAQList = useCallback(async () => {
		setOperationStatus({ type: "loading", operation: "fetch" });

		try {
			const response = await VolunteersFAQService.getVolunteersFAQList();

			if (response.isSuccessful) {
				setVolunteersFAQList(response.result?.items ?? []);
			} else {
				message.error(getResponseErrorMessage(response));
			}
		} catch (error) {
			showError(error);
		} finally {
			setOperationStatus({ type: "idle" });
		}
	}, []);

	const getVolunteersFAQDetails = useCallback(async (id: string) => {
		setOperationStatus({ type: "loading", operation: "fetch" });

		try {
			const response = await VolunteersFAQService.getVolunteersFAQDetails(id);

			if (response.isSuccessful) {
				setVolunteersFAQDetail(response.result);
				setVolunteersFAQEditForm(toVolunteersFAQEditFormData(response.result));
			} else {
				message.error(getResponseErrorMessage(response));
			}
		} catch (error) {
			showError(error);
			if (isNotFoundError(error)) {
				navigate(VOLUNTEERS_FAQ_LIST_PATH);
			}
		} finally {
			setOperationStatus({ type: "idle" });
		}
	}, [navigate]);

	const addVolunteersFAQ = useCallback(async () => {
		if (isMutatingRef.current) {
			return;
		}

		const validationError = VolunteersFAQValidator.validateAddVolunteersFAQ(volunteersFAQForm);
		if (validationError) {
			message.info(validationError);
			return;
		}

		isMutatingRef.current = true;
		setOperationStatus({ type: "loading", operation: "add" });

		try {
			const response = await VolunteersFAQService.addVolunteersFAQ(volunteersFAQForm);

			if (response.isSuccessful) {
				message.success("Merak edilen soru başarıyla eklendi.");
				setVolunteersFAQForm(getDefaultVolunteersFAQFormData());
				navigate(VOLUNTEERS_FAQ_LIST_PATH);
			} else {
				message.error(getResponseErrorMessage(response));
			}
		} catch (error) {
			showError(error);
		} finally {
			isMutatingRef.current = false;
			setOperationStatus({ type: "idle" });
		}
	}, [volunteersFAQForm, navigate]);

	const updateVolunteersFAQ = useCallback(async () => {
		if (isMutatingRef.current) {
			return;
		}
		if (!volunteersFAQEditForm || !volunteersFAQDetail) {
			message.error(NOT_FOUND_MESSAGE);
			return;
		}

		const updateRequest = buildVolunteersFAQUpdateRequest(
			volunteersFAQEditForm,
			toVolunteersFAQEditFormData(volunteersFAQDetail)
		);

		const validationError = VolunteersFAQValidator.validateUpdateVolunteersFAQ(updateRequest);
		if (validationError) {
			message.info(validationError);
			return;
		}

		if (Object.keys(updateRequest).length === 1) {
			message.info("Herhangi bir değişiklik yapılmadı.");
			return;
		}

		isMutatingRef.current = true;
		setOperationStatus({ type: "loading", operation: "update" });

		try {
			const response = await VolunteersFAQService.updateVolunteersFAQ(updateRequest);

			if (response.isSuccessful) {
				message.success("Merak edilen soru başarıyla güncellendi.");
				clearVolunteersFAQFormData();
				navigate(VOLUNTEERS_FAQ_LIST_PATH);
			} else {
				message.error(getResponseErrorMessage(response));
			}
		} catch (error) {
			showError(error);
			if (isNotFoundError(error)) {
				navigate(VOLUNTEERS_FAQ_LIST_PATH);
			}
		} finally {
			isMutatingRef.current = false;
			setOperationStatus({ type: "idle" });
		}
	}, [volunteersFAQEditForm, volunteersFAQDetail, navigate, clearVolunteersFAQFormData]);

	const removeVolunteersFAQ = useCallback(async (id: string) => {
		if (isMutatingRef.current) {
			return;
		}

		isMutatingRef.current = true;
		setOperationStatus({ type: "loading", operation: "delete" });

		try {
			const response = await VolunteersFAQService.removeVolunteersFAQ(id);

			if (response.isSuccessful) {
				message.success("Merak edilen soru başarıyla silindi.");
			} else {
				message.error(getResponseErrorMessage(response));
			}
		} catch (error) {
			showError(error);
		} finally {
			isMutatingRef.current = false;
			setOperationStatus({ type: "idle" });
		}

		// The backend re-numbers the remaining questions, so the list is refreshed after every attempt.
		await getVolunteersFAQList();
	}, [getVolunteersFAQList]);

	const contextValue = useMemo<VolunteersFAQContextState>(
		() => ({
			volunteersFAQList,
			volunteersFAQDetail,
			volunteersFAQForm,
			volunteersFAQEditForm,
			operationStatus,
			setVolunteersFAQForm,
			setVolunteersFAQEditForm,
			clearVolunteersFAQFormData,
			clearVolunteersFAQList,
			getVolunteersFAQList,
			getVolunteersFAQDetails,
			addVolunteersFAQ,
			updateVolunteersFAQ,
			removeVolunteersFAQ,
		}),
		[
			volunteersFAQList,
			volunteersFAQDetail,
			volunteersFAQForm,
			volunteersFAQEditForm,
			operationStatus,
			clearVolunteersFAQFormData,
			clearVolunteersFAQList,
			getVolunteersFAQList,
			getVolunteersFAQDetails,
			addVolunteersFAQ,
			updateVolunteersFAQ,
			removeVolunteersFAQ,
		]
	);

	return <VolunteersFAQContext.Provider value={contextValue}>{children}</VolunteersFAQContext.Provider>;
};
